import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { getTarget, isItUndefined } from '../../util/DataUtil';
import { cleanHTML } from '../utils/htmlUtils';
import { Each } from '../../util/Each';
import { Constants } from '../../util/Constants';
import Link from 'next/link';

const LeadershipBlock = ({ data, width }) => {
  const [leadership, setLeadership] = useState(
    data?.field_leadership_paragraph?.leadership_paragraph_type
  );
  const descriptionRefs = useRef([]);

  useEffect(() => {
    leadership.forEach((item, index) => {
      if (descriptionRefs.current[index]) {
        const descriptionElement = descriptionRefs.current[index];

        // Removing <p> tags with only whitespace
        const paragraphs = descriptionElement.querySelectorAll('p');
        paragraphs.forEach((p) => {
          if (p.innerHTML.trim() === '' || p.innerHTML.trim() === '&nbsp;') {
            p.remove();
          }
        });

        const height = descriptionElement.scrollHeight;
        if (item.expanded) {
          descriptionElement.style.maxHeight = `${height}px`;
          descriptionElement.classList.add('removeShadow');
        } else {
          descriptionElement.style.maxHeight = '150px';
          descriptionElement.classList.remove('removeShadow');
        }
      }
    });
  }, [leadership]);

  const getImageSize = () => {
    if (!width) {
      return { width: 400, height: 350 };
    }

    if (width <= 416) {
      return { width: 366, height: 306 };
    } else if (width <= 800) {
      return { width: 305, height: 255 };
    } else {
      return { width: 368, height: 258 };
    }
  };

  const toggleExpanded = (itemId) => {
    const updatedItems = [...leadership];
    const index = updatedItems.findIndex((item) => item?.id === itemId);

    if (index !== -1) {
      updatedItems[index] = {
        ...updatedItems[index],
        expanded: !updatedItems[index]?.expanded,
      };
      setLeadership(updatedItems);
    }
  };

  return (
    <div id={'leadership'} className={'inner-container'}>
      <Each
        of={leadership}
        render={(item, index) => (
          <>
            <div className={'leadership-wrapper'} key={index}>
              <div className={'image-wrapper'}>
                <Image
                  priority
                  // Trim each part BEFORE joining. Several leadership entries
                  // carry trailing whitespace in the CMS (e.g. the name is
                  // stored as "Greg Wright " / "Chris Sharp  "), which the
                  // `', '` join turned into a space before the comma:
                  // alt="Greg Wright , Chief Investment Officer, Digital Realty".
                  // Normalising here rather than editing the individual CMS
                  // values means any future entry with stray whitespace is
                  // handled too.
                  //
                  // The trim also has to run BEFORE `filter(Boolean)`, so a
                  // field holding only whitespace is dropped instead of
                  // producing an empty segment (", , Digital Realty").
                  alt={[
                    isItUndefined(item?.field_leadership_title),
                    isItUndefined(item?.field_leadership_subtitle),
                    'Digital Realty',
                  ]
                    .map((part) =>
                      typeof part === 'string' ? part.trim() : part
                    )
                    .filter(Boolean)
                    .join(', ')}
                  src={item?.field_leadership_image?.[0]}
                  width={getImageSize()?.width}
                  height={getImageSize()?.height}
                  className={'image'}
                />
              </div>
              <div className={'main-wrapper'}>
                <div className={'header-wrapper'}>
                  <div>
                    <p className={'title'}>
                      {isItUndefined(item?.field_leadership_title)}
                    </p>
                    <p className={'subtitle'}>
                      {isItUndefined(item?.field_leadership_subtitle)}
                    </p>
                  </div>
                  <div className={'social-wrapper'}>
                    <Each
                      of={item?.field_leadership_social?.social_icons_paragraph}
                      render={(socialItem, socialIndex) => (
                        <div className={'social'} key={socialIndex}>
                          <Link
                            href={isItUndefined(socialItem?.field_social_link)}
                            passHref
                          >
                            <a
                              target={getTarget(
                                isItUndefined(socialItem?.field_social_link)
                              )}
                            >
                              <img
                                className={'social-icon'}
                                src={
                                  Constants.cloudinaryImage +
                                  socialItem?.field_social_icon?.webimage
                                }
                                alt="social-image"
                                width={48}
                                height={48}
                              />
                            </a>
                          </Link>
                        </div>
                      )}
                    />
                  </div>
                </div>
                <div className={'text-wrapper'}>
                  <span
                    ref={(el) => (descriptionRefs.current[index] = el)}
                    className={'description'}
                    dangerouslySetInnerHTML={{
                      __html: cleanHTML(
                        isItUndefined(item?.field_leadership_description)
                      ),
                    }}
                  />
                  <div
                    className={item?.expanded ? 'see-less' : 'read-more'}
                    onClick={() => toggleExpanded(item?.id)}
                  >
                    {item?.expanded
                      ? isItUndefined(data?.field_see_less_label)
                      : isItUndefined(data?.field_read_more_label)}
                    <img
                      alt="more-arrow-icon"
                      className={'arrow'}
                      src={'/images/learn-more-arrow.svg'}
                    />
                  </div>
                </div>
              </div>
            </div>
            <hr />
          </>
        )}
      />
    </div>
  );
};

export default LeadershipBlock;
